import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const COLOR = '#3b82f6';
const EM_DASH = '—';

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatCompactMoney = (value, currency, locale) =>
  new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const NbvTooltip = ({ active, payload, currency, t }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: 12,
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      fontSize: '0.85rem',
    }}>
      <div className="fw-bold mb-2">{point.periodLabel}</div>
      <div className="d-flex align-items-center gap-2">
        <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: COLOR, display: 'inline-block' }} />
        <span className="text-muted">{t('shared.nbv', 'Net Book Value')}</span>
        <span className="fw-bold ms-auto">{formatMoney(point.nbv, currency)}</span>
      </div>
    </div>
  );
};

const NbvDepreciationTrendWidget = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const points = data?.depreciationTrend || [];
    return points.map((p) => ({
      periodLabel: new Date(p.period).toLocaleDateString(i18n.language || 'en-US', { month: 'short', year: 'numeric' }),
      nbv: Math.round(Number(p.totalNbv) || 0),
    }));
  }, [data, i18n.language]);

  const currentNbv = data?.realTimeCards?.assetNetBookValue;
  const hasCurrentNbv = currentNbv !== null && currentNbv !== undefined;
  const isEmpty = chartData.length === 0 && !hasCurrentNbv;

  return (
    <ReportWidget
      title={t('business.inventory.asset_nbv_depreciation_trend', 'Asset Net Book Value Depreciation Trend')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.nbv_trend.empty', 'No depreciation data to display.')}
      onRefresh={handleRefresh}
    >
      <div className="d-flex justify-content-center gap-5 mb-4 py-2">
        <div className="text-center">
          <h3 className="mb-2 fw-bold" style={{ fontSize: '1.75rem' }}>
            {hasCurrentNbv ? formatMoney(currentNbv, currency) : EM_DASH}
          </h3>
          <span
            className="px-3 py-1 rounded-pill d-inline-block text-center"
            style={{ border: `2px solid ${COLOR}`, color: COLOR, fontSize: '0.8rem', fontWeight: 500, minWidth: 90 }}
          >
            {t('business.inventory.asset_net_book_value', 'Asset Net Book Value')}
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="colorNbvReporting" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLOR} stopOpacity={0.6} />
              <stop offset="95%" stopColor={COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => formatCompactMoney(v, currency, i18n.language)}
            width={80}
          />
          <Tooltip content={<NbvTooltip currency={currency} t={t} />} />
          <Area
            type="monotone"
            dataKey="nbv"
            stroke={COLOR}
            strokeWidth={2}
            fill="url(#colorNbvReporting)"
            name={t('shared.nbv', 'Net Book Value')}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default NbvDepreciationTrendWidget;
